import React, { useState } from 'react';
import { SelectedPurchaseType } from '../../../types/types';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { PurchaseContent } from './PurchaseContent';
import { Box, Modal, useMediaQuery } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import ExclusiveOfferInjected from '../Giveaway/components/UpsellModal';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

// Scoped dark (arena) theme for the payment modal — so the MUI form controls
// (radios, labels, checkboxes, buttons) read correctly on the dark surface
// instead of rendering dark-on-dark from the app's default light theme.
const modalTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#38e8ff' },
    error: { main: '#ff6b6b' },
    background: { paper: '#0b1119', default: '#070b11' },
    text: { primary: '#e8f4fb', secondary: '#8ea3b3' }
  }
});

type PurchaseModalProps = {
  isModalOpen: boolean;
  handlePurchaseModalClose: () => void;
  selectedPurchase: SelectedPurchaseType | null;
  handleBonusPurchaseSuccess: () => void;
  showFinalUpsell: boolean;
};

export const PurchaseModal = ({
  isModalOpen,
  handlePurchaseModalClose,
  selectedPurchase,
  handleBonusPurchaseSuccess,
  showFinalUpsell
}: PurchaseModalProps) => {
  const [paymentIntentIdAfterPayment, setPaymentIntentIdAfterPayment] =
    useState<string>('');
  const isMobile = useMediaQuery('(max-width: 600px)');
  const purchaseModalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'linear-gradient(180deg, #0b1119, #070b11)',
    border: '1px solid rgba(56, 232, 255, 0.28)',
    borderRadius: '16px',
    boxShadow: '0 30px 80px -20px rgba(0, 0, 0, 0.85)',
    color: '#e8f4fb',
    p: 4
  };

  return (
    <Elements
      stripe={stripePromise}
      options={{
        mode:
          selectedPurchase?.type === 'Standalone' ||
          selectedPurchase?.type === 'Promotion'
            ? 'payment'
            : 'subscription',
        amount: 200,
        currency: 'aud',
        paymentMethodTypes: ['card'],
        setupFutureUsage:
          selectedPurchase?.type === 'Standalone' ||
          selectedPurchase?.type === 'Promotion'
            ? 'on_session'
            : 'off_session',
        appearance: {
          theme: 'night',
          variables: {
            colorPrimary: '#38e8ff',
            colorBackground: '#0e151f',
            colorText: '#e8f4fb',
            colorTextSecondary: '#8ea3b3',
            colorDanger: '#ff6b6b',
            borderRadius: '10px'
          },
          rules: {
            '.Label': { color: '#8ea3b3' },
            // White input fields with dark text so card details are easy to
            // read against the dark modal (the faint dark-on-dark was hard to see).
            '.Input': {
              backgroundColor: '#ffffff',
              color: '#0e151f',
              border: '1px solid #ccd6e0'
            },
            '.Input::placeholder': { color: '#7a8792' },
            '.Input:focus': {
              border: '1px solid #38e8ff',
              boxShadow: '0 0 0 2px rgba(56, 232, 255, 0.45)'
            },
            '.Input--invalid': { color: '#0e151f', borderColor: '#ff6b6b' }
          }
        }
      }}
    >
      <Modal open={isModalOpen && !!selectedPurchase}>
        <Box
          sx={{
            ...purchaseModalStyle,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: isMobile ? '100%' : 'min(880px, 94vw)',
            maxHeight: isMobile ? '100%' : '90vh',
            overflowY: 'auto',
            padding: '24px'
          }}
        >
          <ThemeProvider theme={modalTheme}>
            {!showFinalUpsell && (
              <PurchaseContent
                handlePurchaseModalClose={handlePurchaseModalClose}
                selectedPurchase={selectedPurchase}
                handleBonusPurchaseSuccess={handleBonusPurchaseSuccess}
                setPaymentIntentIdAfterPayment={setPaymentIntentIdAfterPayment}
              />
            )}
            {!!showFinalUpsell && (
              <ExclusiveOfferInjected
                paymentIntentId={paymentIntentIdAfterPayment}
                handleCloseModal={handlePurchaseModalClose}
              />
            )}
          </ThemeProvider>
        </Box>
      </Modal>
    </Elements>
  );
};
